import { memo } from "react";
import { Link } from "react-router-dom";
import { cn } from "../../../../lib/cn";
import { Skeleton } from "../../../../components/shared";
import {
  ProjectsIcon,
  ChatIcon,
} from "../../../../assets/icons";
import { ContractGenerateIcon } from "../../../../assets/icons/ContractGenerate";
import { DraftFlowNavigator } from "./DraftFlowNavigator";
import { SessionList } from "./SessionList";

const HistoryTrigger = ({ isOpen, activeSession, sessionCount, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors cursor-pointer",
      isOpen
        ? "border-primary/30 bg-primary/5 text-primary"
        : "border-surface-border bg-white text-grey hover:border-surface-border hover:bg-surface",
    )}
  >
    <ChatIcon width={15} height={15} className="shrink-0 text-mediumgrey" />
    <span className="max-w-[140px] truncate">
      {activeSession?.title || "Chat History"}
    </span>
    {sessionCount.length > 0 && (
      <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-primary/10 text-[10px] font-semibold text-primary/70 px-1">
        {sessionCount.length}
      </span>
    )}
    <svg
      width={10}
      height={10}
      viewBox="0 0 10 10"
      className={cn(
        "shrink-0 text-mediumgrey transition-transform",
        isOpen && "rotate-180",
      )}
    >
      <path
        d="M2 3.5L5 6.5L8 3.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  </button>
);

const SessionHistoryMenu = ({
  isOpen,
  setIsOpen,
  activeSession,
  sessionCount,
  onSessionSelect,
  onCreateSession,
  creatingSession,
}) => (
  <div className="relative">
    <HistoryTrigger
      isOpen={isOpen}
      activeSession={activeSession}
      sessionCount={sessionCount}
      onClick={() => setIsOpen(!isOpen)}
    />
    {isOpen && (
      <div
        role="dialog"
        aria-label="Chat history"
        className="absolute right-0 top-full z-[70] mt-2 flex w-[min(320px,calc(100vw-2rem))] max-h-[520px] flex-col overflow-hidden rounded-xl border border-surface-border bg-white shadow-[0_18px_60px_rgba(15,23,42,0.12),0_4px_14px_rgba(15,23,42,0.08)]"
      >
        <div className="flex items-center justify-between border-b border-surface-border bg-surface/50 px-3 py-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-mediumgrey">
            Sessions
          </span>
          <button
            type="button"
            className="rounded-md bg-primary px-2.5 py-1 text-[11px] font-medium text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => {
              onCreateSession?.();
              setIsOpen(false);
            }}
            disabled={creatingSession}
          >
            New chat
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          <SessionList
            onSelect={(id) => {
              onSessionSelect?.(id);
              setIsOpen(false);
            }}
          />
        </div>
      </div>
    )}
  </div>
);

const IntentChip = ({ intentData }) => {
  if (!intentData) return null;
  return (
    <span
      className="hidden lg:inline-flex items-center gap-1.5 rounded-full bg-indigo-50/80 px-2 py-0.5 text-[11px] font-medium text-indigo-800 max-w-[200px]"
      title={intentData?.description || intentData?.title || "Untitled"}
    >
      <span className="truncate">
        {intentData?.title || "Untitled"}
      </span>
      {intentData?.intent_changed ? (
        <span className="shrink-0 bg-indigo-200/60 text-indigo-600 text-[8px] rounded-full font-bold uppercase px-1">
          Updated
        </span>
      ) : null}
    </span>
  );
};

export const DraftFlowHeader = memo(
  ({
    isDraftFlowRoute,
    isStandaloneDraftFlow,
    resolvedProjectName,
    projectOptions,
    selectedProjectId,
    projectsLoading,
    onProjectChange,
    sessions,
    activeSession,
    onSessionSelect,
    onCreateSession,
    creatingSession,
    sessionCount,
    sessionDetailLoading,
    isSessionPopoverOpen,
    setIsSessionPopoverOpen,
    summaryMeta,
    intentData,
  }) => {
    if (isDraftFlowRoute) {
      return (
        <div className="min-h-[56px] lg:min-h-[70px] px-4 md:px-6 py-3 border-b border-surface-border flex items-center sticky top-0 z-50 bg-white">
          <div className="flex flex-row w-full justify-between items-center gap-2 flex-wrap">
            <div className="font-sans flex items-center gap-3 w-full sm:w-auto">
              {isStandaloneDraftFlow ? (
                <>
                  <div className="flex items-center gap-2">
                    <ContractGenerateIcon
                      width={18}
                      height={18}
                      className="text-primary/60"
                    />
                    <span className="text-sm font-semibold text-primary">
                      Draft Flow
                    </span>
                  </div>
                  <DraftFlowNavigator
                    projectOptions={projectOptions}
                    selectedProjectId={selectedProjectId}
                    projectsLoading={projectsLoading}
                    onProjectSelect={onProjectChange}
                    sessions={sessions}
                    activeSession={activeSession}
                    onSessionSelect={onSessionSelect}
                    onCreateSession={onCreateSession}
                    creatingSession={creatingSession}
                    summaryMeta={summaryMeta}
                  />
                  <IntentChip intentData={intentData} />
                </>
              ) : resolvedProjectName ? (
                <>
                  <ProjectsIcon
                    width={30}
                    height={30}
                    className="text-black flex-shrink-0"
                  />
                  <div className="flex items-center gap-2 min-w-0 overflow-hidden">
                    <Link
                      to="/projects/all"
                      className="text-sm text-grey hover:text-secondary-dark transition shrink-0"
                    >
                      Projects
                    </Link>
                    <span className="text-grey hidden sm:inline">/</span>
                    <span className="text-sm font-semibold truncate">
                      {resolvedProjectName}
                    </span>
                    <span className="text-grey hidden sm:inline">/</span>
                    <span className="text-sm hidden sm:inline">Draft Flow</span>
                  </div>
                </>
              ) : (
                <span className="text-sm font-semibold text-primary">
                  Draft Flow
                </span>
              )}
            </div>
            <div className="font-sans flex items-center gap-1.5 w-full sm:w-auto justify-end">
              {!isStandaloneDraftFlow && (
                <SessionHistoryMenu
                  isOpen={isSessionPopoverOpen}
                  setIsOpen={setIsSessionPopoverOpen}
                  activeSession={activeSession}
                  sessionCount={sessionCount}
                  onSessionSelect={onSessionSelect}
                  onCreateSession={onCreateSession}
                  creatingSession={creatingSession}
                />
              )}
            </div>
          </div>
        </div>
      );
    }

    // Non-draft-flow route header (embedded in projects)
    return (
      <div className="flex items-center justify-between border-b border-surface-border px-4 md:px-6 py-3 min-h-[56px]">
        <div className="flex items-center gap-3 overflow-hidden">
          <div className="flex flex-col overflow-hidden">
            <p className="text-sm font-semibold text-primary truncate flex items-center gap-2 text-balance">
              {activeSession?.title || "Draft flow"}
            </p>
            <p className="text-[10px] text-mediumgrey truncate text-pretty">
              {resolvedProjectName || "General session"}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {sessionDetailLoading && (
            <Skeleton className="h-6 w-24 rounded" />
          )}
        </div>
      </div>
    );
  },
);

DraftFlowHeader.displayName = "DraftFlowHeader";
